import { useQuery } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  RefreshCw,
  Wrench,
  MapPin,
  User,
} from 'lucide-react';
import { useState } from 'react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

function getDefaultDateRange() {
  const now = new Date();
  const from = new Date(now);
  // Start from current week Monday
  const day = from.getDay();
  from.setDate(from.getDate() - ((day + 6) % 7));
  const to = new Date(from);
  to.setDate(to.getDate() + 13); // 2 weeks
  return {
    from: from.toISOString().split('T')[0],
    to: to.toISOString().split('T')[0],
  };
}

function BookingCard({ booking }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 text-sm space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-brand-dark">{booking.customerName}</span>
        {booking.workOrderNumber && (
          <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-mono">
            #{booking.workOrderNumber}
          </span>
        )}
      </div>
      {booking.assetName && (
        <p className="text-xs text-brand-neutral">
          {booking.assetName}{booking.assetSerialNumber ? ` (${booking.assetSerialNumber})` : ''}
        </p>
      )}
      <div className="flex flex-wrap gap-2 text-[11px] text-brand-neutral">
        <span className="flex items-center gap-1 font-medium text-brand-dark">
          <Clock className="w-3 h-3" /> {booking.startTime} – {booking.endTime}
        </span>
        {booking.mechanic && (
          <span className="flex items-center gap-1">
            <Wrench className="w-3 h-3" /> {booking.mechanic}
          </span>
        )}
        {booking.workshopLocation && (
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {booking.workshopLocation}
          </span>
        )}
      </div>
    </div>
  );
}

function ConflictCard({ conflict }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="rounded-xl border border-red-200 bg-red-50/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 transition-colors"
      >
        <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-red-600">Timing Overlap</span>
            <span className="text-[11px] text-red-500/70">{formatDate(conflict.date)}</span>
          </div>
          <p className="text-sm font-medium text-red-800 truncate">{conflict.description}</p>
        </div>
        <span className="flex items-center gap-1 text-[11px] font-semibold text-red-600/70">
          {conflict.bookings.length} jobs
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 grid gap-2 sm:grid-cols-2">
          {conflict.bookings.map((b, i) => (
            <BookingCard key={b.jobUid || i} booking={b} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DoubleBookingDetection() {
  const { selectedLocationId, currentLocation } = useLocationContext();
  const defaults = getDefaultDateRange();
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);

  const { data, isLoading, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['double-bookings', from, to, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/double-bookings', { params: { from, to } });
      return data;
    },
    enabled: !!from && !!to,
    // Conflicts get resolved elsewhere (reassignment in AMC or edits in Zuper),
    // so this page must never sit on a stale snapshot showing a conflict that
    // is already fixed. The app disables focus-refetch globally, so opt back in
    // here and always refetch on mount.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const conflicts = data?.conflicts || [];

  return (
    <Layout>
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-red-50 to-white rounded-xl flex items-center justify-center shadow-sm">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Booking Conflicts</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Detect overlapping job timings for mechanics</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          {dataUpdatedAt > 0 && (
            <span className="hidden md:inline text-[11px] text-brand-neutral whitespace-nowrap">
              {isFetching ? 'Checking…' : `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}`}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Fetch live conflicts from Zuper"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-5xl mx-auto space-y-4">
          {/* Date Range */}
          <div className="bg-white rounded-xl border border-brand-light/60 p-4 flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-brand-neutral uppercase tracking-wider mb-1">From</label>
              <input
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                className="border border-brand-light rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-brand-neutral uppercase tracking-wider mb-1">To</label>
              <input
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
                className="border border-brand-light rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-white rounded-xl border border-brand-light/60 p-4 text-center">
              <p className="text-2xl font-bold text-brand-dark">{data?.totalBookings || 0}</p>
              <p className="text-xs text-brand-neutral mt-1">Scheduled Jobs</p>
            </div>
            <div className={`rounded-xl border p-4 text-center ${conflicts.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-2xl font-bold ${conflicts.length > 0 ? 'text-red-600' : 'text-green-600'}`}>{conflicts.length}</p>
              <p className="text-xs text-brand-neutral mt-1">{conflicts.length > 0 ? 'Conflicts Found' : 'No Conflicts'}</p>
            </div>
          </div>

          {/* Conflicts */}
          {isLoading || isFetching ? (
            <div className="bg-white rounded-xl border border-brand-light/60 p-12 text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-brand-primary mx-auto mb-2" />
              <p className="text-sm text-brand-neutral">Checking Zuper for conflicts...</p>
            </div>
          ) : conflicts.length === 0 ? (
            <div className="bg-white rounded-xl border border-brand-light/60 p-12 text-center">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-7 h-7 text-green-500" />
              </div>
              <p className="text-sm font-medium text-brand-dark">No timing conflicts found</p>
              <p className="text-xs text-brand-neutral mt-1">All scheduled jobs have non-overlapping times</p>
            </div>
          ) : (
            <div className="space-y-3">
              {conflicts.map((conflict, i) => (
                <ConflictCard key={conflict.key || i} conflict={conflict} />
              ))}
            </div>
          )}
        </div>
      </main>
    </Layout>
  );
}
